import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { deleteNotification, refreshLotNotificationsList, refreshNotificationsList, setAllNotificationsAsRead, setNotificationAsRead, togglePanel } from './store/NotificationPanel'
import { Avatar, Backdrop, IconButton, Menu, MenuItem, Badge, Tooltip, Drawer, Skeleton } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { makeStyles, withStyles } from "tss-react/mui";
import { PrembidLocalStorage } from '@/lib/utils/PrembidLocalStorage';
import {
    MailOutlined,
    DraftsOutlined,
    MoreVert as MoreVertIcon,
    Launch as LaunchIcon
} from '@mui/icons-material'
import { grey, red } from '@mui/material/colors';
import ns from '@/lib/utils/NotificationService';
import { FaRedo } from "react-icons/fa";
import clsx from 'clsx';
import { formatDateTime } from '../../lib/utils/General';
import useHistory from '../../lib/hooks/useHistory';

const StyledBadge = withStyles(Badge, {
    badge: {
        top: "12px",
        right: "15px",
        backgroundColor: "var(--danger)",
        color: "var(--danger)",
        boxShadow: "0 0 0 2px #fff",
        "&::after": {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            animation: "$ripple 1.2s infinite ease-in-out",
            border: "1px solid currentColor",
            content: '""',
        },
    }
});

const useStyles = makeStyles()((theme) => ({
    read: {
        backgroundColor: grey[300],
    },
    unread: {
        backgroundColor: red[400],
    },
    cardHeader: {
    },
    drawer: {

    },
    tabsContainer: {
        flexGrow: 1,
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    inline: {
        display: 'inline',
    },
    placeHolder: {
        width: 50,
        height: 50,
        color: grey[300]
    },
    activeNotificationTitle: {
        maxWidth: 200
    },
    activeNotificationCard: {
        maxWidth: 345,
    },
    activeNotificationImage: {
        height: 0,
        paddingTop: '56.25%', // 16:9
    },
    activeNotificationExpand: {
        transform: 'rotate(0deg)',
        marginLeft: 'auto',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    activeNotificationExpandOpen: {
        transform: 'rotate(180deg)',
    }
}));

const LimitedBackdrop = withStyles(Backdrop, {
    root: {
        position: "fixed",
        zIndex: 1
    }
});

interface NotificationPanelProps {
    open: boolean,
    list: any[],
    activeLotList: any[],
    unreadNotificationCount: number,
    generalUnreadCount: number,
    pagination: any,
    loading: boolean,
    lastRefreshed: string | undefined,
    newMessages: boolean,
    togglePanel_d: (status) => void,
    settings: any,
    refreshNotifications_d: (profileId: string, pagination: any, includeRead: boolean, onCompletedCallback: (response) => void, withReplacement?: boolean) => void,
    refreshLotNotifications_d: (profileId: string, lotId: string | undefined, pagination: any, includeRead: boolean, onCompletedCallback: (response) => void) => void,
    setNotificationAsRead_d: (notificationId: string, onCompletedCallback: (response: any) => void) => void,
    setAllNotificationsAsRead_d: (profileId: string, onCompletedCallback: (response: any) => void) => void,
    deleteNotification_d: (notificationId: string, onCompletedCallback: (response: any) => void) => void
}

const NotificationPanel: React.FC<NotificationPanelProps> = props => {
    const { open,
        list,
        activeLotList,
        pagination,
        unreadNotificationCount,
        generalUnreadCount,
        loading,
        lastRefreshed,
        newMessages,
        togglePanel_d,
        settings,
        refreshNotifications_d,
        refreshLotNotifications_d,
        setNotificationAsRead_d,
        setAllNotificationsAsRead_d,
        deleteNotification_d
    } = props;

    const TabNames = {
        Lots: `${settings.LotName}s`,
        History: 'All'
    }

    const [selectedTab, setSelectedTab] = useState<any>(TabNames.Lots);
    const { classes } = useStyles();

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setOpenMenu] = useState(undefined);

    const history = useHistory();

    useEffect(() => {
        if (open) {
            handleRefresh();
        }
    }, [open])

    const handleClose = (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }

        togglePanel_d(false);
    };

    const handleMenu = (event, id) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setOpenMenu(undefined);
    };

    const handleViewLot = (path) => {
        handleMenuClose();
        togglePanel_d(false);
        let timestamp = Date.now().valueOf();
        history.push(`${path}?ts=${timestamp}`);
    };

    const handleViewAuction = (path) => {
        handleMenuClose();
        togglePanel_d(false);
        let timestamp = Date.now().valueOf();
        history.push(`${path}?ts=${timestamp}`);
    };

    const handleSetAllNotificationsAsRead = () => {
        let profileId = PrembidLocalStorage.currentProfile;
        if (profileId && profileId !== '') {
            setAllNotificationsAsRead_d(profileId, (response) => {
                handleRefresh();
            });
        }
    }

    const handleSetNotificationAsRead = (notificationId) => {
        if (notificationId && notificationId !== '') {
            handleMenuClose();
            setNotificationAsRead_d(notificationId, (response) => {
                if (response.success === false)
                    ns.error('', "Failed to to mark the notification as read");
            });
        }
    }

    const handleDeleteNotification = (notificationId) => {
        if (notificationId && notificationId !== '') {
            handleMenuClose();
            deleteNotification_d(notificationId, (response) => {
                if (response.success === false)
                    ns.error('', "Failed to the delete the notification");
            });
        }
    }

    const handleLoadMoreNotifications = () => {
        let profileId = PrembidLocalStorage.currentProfile;
        if (profileId && profileId !== '') {
            let tempPagination = { ...pagination };
            tempPagination.currentPage = pagination.currentPage + 1;
            refreshNotifications_d(profileId, tempPagination, true, (response) => {
                //TODO: Handle response if needed
            }, false);
        }
    }


    // One notification as a card (mobile NotificationCard): read/unread icon, title, message, date, menu.
    const renderHistoricNotification = (item, showViewLotInMenu: boolean = true, showViewAuctionInMenu: boolean = true): JSX.Element => (
        <div className={"pb-note" + (item.isRead ? "" : " pb-note--unread")} key={item.id}>
            <span className="pb-note-icon">{item.isRead ? <DraftsOutlined fontSize="small" /> : <MailOutlined fontSize="small" />}</span>
            <div className="pb-note-body">
                <div className="pb-note-title">{item.jsonData?.title}</div>
                {item.jsonData?.message && <div className="pb-note-message">{item.jsonData?.message}</div>}
                <div className="pb-note-date">{formatDateTime(item.createdAt, "yyyy/MM/dd HH:mm")}</div>
            </div>
            <IconButton size="small" className="pb-note-menu" aria-label="Notification options" onClick={(event) => handleMenu(event, item.id)}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={menuOpen === item.id}
                onClose={handleMenuClose}
            >
                {!item.isRead && <MenuItem onClick={() => handleSetNotificationAsRead(item.id)}>Mark as Read</MenuItem>}
                {showViewAuctionInMenu && item.jsonData?.auctionId && <MenuItem onClick={() => handleViewAuction(`/Lots/${item.jsonData?.auctionId}`)}>{"View " + settings.AuctionName}</MenuItem>}
                {showViewLotInMenu && item.jsonData?.lotBiddingLink && <MenuItem onClick={() => handleViewLot(item.jsonData?.lotBiddingLink)}>{"View " + settings.LotName}</MenuItem>}
                <MenuItem onClick={() => handleDeleteNotification(item.id)}>Delete</MenuItem>
            </Menu>
        </div>
    )

    // A lot with its notifications (mobile SectionHeader + cards).
    const renderActiveLotNotification = (item): JSX.Element => {
        const firstNotification = item.notifications[0];
        return (
            <section className="pb-note-group" key={item.id}>
                <div className="pb-note-group-head">
                    <Avatar src={item.imageUrl} sx={{ width: 36, height: 36 }}>{settings.LotName?.[0]}</Avatar>
                    <div className="pb-note-group-title" title={item.name}>{item.name}</div>
                    <IconButton size="small" aria-label={"View " + settings.LotName} onClick={() => handleViewLot(firstNotification.jsonData?.lotBiddingLink)}>
                        <LaunchIcon fontSize="small" />
                    </IconButton>
                </div>
                {item.notifications?.map(x => renderHistoricNotification(x, false, false))}
            </section>
        );
    }

    const renderEmpty = (text: string) => (
        <div className="pb-note-empty">
            <DraftsOutlined />
            <span>{text}</span>
        </div>
    );

    // Skeleton cards while refreshing (instead of a spinner over the list).
    const renderSkeletons = () => (
        <>
            {[0, 1, 2, 3].map((i) => (
                <div className="pb-note" key={"sk" + i}>
                    <Skeleton animation="wave" variant="circular" width={36} height={36} />
                    <div className="pb-note-body">
                        <Skeleton animation="wave" variant="text" width="60%" height={18} />
                        <Skeleton animation="wave" variant="text" width="90%" height={16} />
                        <Skeleton animation="wave" variant="text" width="35%" height={14} />
                    </div>
                </div>
            ))}
        </>
    );

    const renderHistoricNotificationsList = (): JSX.Element => (
        <>
            {(list?.length || 0) === 0 && !loading && renderEmpty("No notifications")}
            {list.map((item) => renderHistoricNotification(item))}
            {(pagination.total || 0) > 0 && <div className="pb-note-count">Displaying {list.length} of {pagination.total} notifications</div>}
            {(pagination.total || 0) > 10 && pagination.total !== list.length && (
                <button type="button" className="pb-activity-reset pb-note-more" onClick={() => handleLoadMoreNotifications()}>Load more</button>
            )}
        </>
    );

    const renderActiveLotNotificationsList = (): JSX.Element => (
        <>
            {(activeLotList?.length || 0) === 0 && !loading && renderEmpty("No " + settings.LotName?.toLowerCase() + " notifications")}
            {activeLotList.map((item) => renderActiveLotNotification(item))}
        </>
    );

    const tabs = [
        { key: TabNames.Lots, dot: unreadNotificationCount > generalUnreadCount },
        { key: TabNames.History, dot: generalUnreadCount > 0 },
    ];

    const handleRefresh = (refreshHistory: boolean = true) => {
        if (open) {
            let profileId = PrembidLocalStorage.currentProfile;
            if (profileId && profileId !== '') {
                refreshLotNotifications_d(profileId, undefined, undefined, true, (response) => {
                    if (response.success === false)
                        ns.error('', "Failed to to refresh the lot notifications");

                    if (refreshHistory) {
                        refreshNotifications_d(profileId, undefined, true, (response) => {
                            if (response.success === false)
                                ns.error('', "Failed to to refresh the notifications");
                        });
                    }
                });
            }
        }
    }

    return (<>
        <Drawer anchor='right' open={open} onClose={handleClose} classes={{ paper: "pb-notes-panel" }}>
            <div className="pb-notes-head">
                <div style={{ minWidth: 0 }}>
                    <h3>Notifications</h3>
                    <small>{(lastRefreshed && !loading) ? "Last refreshed at " + lastRefreshed : "Refreshing notifications..."}</small>
                </div>
                <div className="pb-notes-head-actions">
                    <Tooltip open={newMessages} title={newMessages ? "New message(s)." : ""} placement="bottom">
                        <button type="button" className={"pb-activity-reset" + (newMessages ? " pb-notes-refresh--new" : "")} onClick={() => handleRefresh()} disabled={loading}>
                            <FaRedo size={12} /> Refresh
                        </button>
                    </Tooltip>
                    <IconButton aria-label="Close" className="pb-auth-close pb-notes-close" onClick={() => togglePanel_d(false)}>
                        <MdClose size={20} />
                    </IconButton>
                </div>
            </div>

            <ul className="pb-tabs MuiList-root pb-notes-tabs" role="tablist">
                {tabs.map((t) => (
                    <li key={t.key} role="tab" aria-selected={selectedTab === t.key} className={"MuiListItem-root" + (selectedTab === t.key ? " Mui-selected" : "")} onClick={() => setSelectedTab(t.key)}>
                        {t.key}
                        {t.dot && <span className="badge-circle" />}
                    </li>
                ))}
            </ul>

            {unreadNotificationCount > 0 && (
                <div className="pb-notes-toolbar">
                    <button type="button" className="pb-activity-reset" onClick={() => handleSetAllNotificationsAsRead()}>Mark all as read</button>
                </div>
            )}

            <div className="pb-notes-list">
                {loading && renderSkeletons()}
                {!loading && selectedTab === TabNames.Lots && renderActiveLotNotificationsList()}
                {!loading && selectedTab === TabNames.History && renderHistoricNotificationsList()}
            </div>
        </Drawer>
    </>);
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        togglePanel_d: (status) => dispatch(togglePanel(status)),
        refreshNotifications_d: (profileId: string, pagination: any, includeRead: boolean, onCompletedCallback: (response) => void, withReplacement?: boolean) => dispatch(refreshNotificationsList(profileId, pagination, includeRead, onCompletedCallback, withReplacement)),
        refreshLotNotifications_d: (profileId: string, lotId: string | undefined, pagination: any, includeRead: boolean, onCompletedCallback: (response) => void) => dispatch(refreshLotNotificationsList(profileId, lotId, pagination, includeRead, onCompletedCallback)),
        setNotificationAsRead_d: (notificationId: string, onCompletedCallback: (response: any) => void) => dispatch(setNotificationAsRead(notificationId, onCompletedCallback)),
        setAllNotificationsAsRead_d: (profileId: string, onCompletedCallback: (response: any) => void) => dispatch(setAllNotificationsAsRead(profileId, onCompletedCallback)),
        deleteNotification_d: (notificationId: string, onCompletedCallback: (response: any) => void) => dispatch(deleteNotification(notificationId, onCompletedCallback))
    }
}

const mapStateToProps = (state: any) => ({
    open: state.notificationPanel.open,
    pagination: state.notificationPanel.pagination,
    list: state.notificationPanel.list,
    activeLotList: state.notificationPanel.activeLotList,
    loading: state.notificationPanel.loading,
    unreadNotificationCount: state.notificationPanel.unreadNotificationCount,
    generalUnreadCount: state.notificationPanel.generalUnreadCount,
    lastRefreshed: state.notificationPanel.lastRefreshed,
    newMessages: state.notificationPanel.newMessages,
    settings: state.settings.settings
})

export default connect(mapStateToProps, mapDispatchToProps)(NotificationPanel);
